import {
  ActionType,
  BasketballAction,
  BasketballActionType,
  BasketballMarker,
  BasketballMiscMarker,
  BasketballMiscType,
  BasketballPossessionAction,
  MarkerType,
  MidPoint,
  PointType,
  StencilType,
} from "../../../../types";
import { convertDegreesToRadians, getActionStencils } from "../../../../utils";
import {
  ActionHandoffStencil,
  ActionPassStencil,
  ActionPlayerStencil,
  ActionPossessionStencil,
  ActionStencil,
  ActionStencilName,
  BaseActionStencil,
  MarkerStencil,
  MarkerStencilBase,
  MotionType,
  StencilMidPoint,
} from "../types";
import { MiscStencilName } from "../types";

const getMarkerStencils = (stencils: (BasketballAction | BasketballMarker)[]): BasketballMarker[] => {
  return stencils.filter((stencil): stencil is BasketballMarker => stencil.stencilType === StencilType.MARKER);
};

/** ================================
 * Get Stencil MidPoints
 ================================ */
const getStencilMidPoints = (midPoints: MidPoint[]): StencilMidPoint[] => {
  return midPoints.map((point): StencilMidPoint => {
    switch (point.pointType) {
      case PointType.ANGLE:
        return { x: point.x, y: point.y, type: "angle" };

      case PointType.CURVE:
        return { x: point.x, y: point.y, type: "curve" };

      default:
        return { x: point.x, y: point.y, type: "curve" };
    }
  });
};

/** ================================
 * Get Stencil Name
 ================================ */
const getStencilName = (action: BasketballAction): ActionStencilName => {
  const { layerType } = action;

  const noOfHashes = action.layerType === BasketballActionType.HASHPASS && action.numberOfHashes;

  switch (layerType) {
    case BasketballActionType.HANDOFF:
      return "HandoffStencil";

    case BasketballActionType.HASHPASS: {
      switch (noOfHashes) {
        case 1:
          return "HashPass1Stencil";

        case 2:
          return "HashPass2Stencil";

        case 3:
          return "HashPass3Stencil";

        case 4:
          return "HashPass4Stencil";

        case 5:
          return "HashPass5Stencil";

        default:
          return "StraightHashpass";
      }
    }

    default:
      return "MultiSegmentStencil";
  }
};

const getPossessionMotionType = (
  action: BasketballPossessionAction
): MotionType.DRIBBLE | MotionType.CUT_RECEIVING_HANDOFF | MotionType.SCREEN_RECEIVING_HANDOFF => {
  switch (action.layerType) {
    case BasketballActionType.DRIBBLE:
      return MotionType.DRIBBLE;

    case BasketballActionType.CUT_RECEIVING_HANDOFF:
      return MotionType.CUT_RECEIVING_HANDOFF;

    case BasketballActionType.SCREEN_RECEIVING_HANDOFF:
      return MotionType.SCREEN_RECEIVING_HANDOFF;
  }
};

const getMiscStencilName = (marker: BasketballMiscMarker): MiscStencilName => {
  switch (marker.miscType) {
    case BasketballMiscType.HOOP:
      return "HoopStencil";
    case BasketballMiscType.BALL_STATIC:
      return "BallStencil";
    case BasketballMiscType.FLAG:
      return "FlagStencil";
    case BasketballMiscType.CONE:
      return "ConeStencil";
  }
};

/** ================================
 * Convert Frame Stencil to DB Stencil
 ================================ */
export const convertBBFrameStencilToDBStencil = (data: (BasketballAction | BasketballMarker)[] | null): string => {
  if (!data) return JSON.stringify([]);

  /** ======== Action Stencils ======== */
  const actionStencils: ActionStencil[] = (getActionStencils(data) as BasketballAction[]).map(
    (action): ActionStencil => {
      const actionStencilBase: BaseActionStencil = {
        id: action.id,
        stencilName: getStencilName(action),
        color: action.color,
        theta: (action.rotate && convertDegreesToRadians(action.rotate)) || undefined,
        start: action.pos,
        end: action.endPos,
        trueEnd: action.endPos,
        midPoints: getStencilMidPoints(action.midPoints),
        animation: { moveStart: action.startTime, moveEnd: action.endTime, userConfigured: true },
      };

      switch (action.actionType) {
        case ActionType.BALL: {
          const passAction: ActionPassStencil = {
            ...actionStencilBase,
            motionType: MotionType.PASS,
            nextPlayer: action.nextPlayer,
            prevPlayer: action.prevPlayer,
            nextBallAction: action.nextBallAction,
            playerActionEnd: action.playerActionEnd,
          };
          return passAction;
        }

        case ActionType.PLAYER: {
          const playerAction: ActionPlayerStencil = {
            ...actionStencilBase,
            nextPlayerAction: action.nextPlayerAction,
            motionType: action.layerType === BasketballActionType.SCREEN ? MotionType.SCREEN : MotionType.CUT,
          };
          return playerAction;
        }

        case ActionType.HANDOFF: {
          const handoffAction: ActionHandoffStencil = {
            ...actionStencilBase,
            motionType: MotionType.HANDOFF,
            prevPlayer: action.prevPlayer,
            nextPlayer: action.nextPlayer,
          };
          return handoffAction;
        }

        case ActionType.POSSESSION: {
          const possessionAction: ActionPossessionStencil = {
            ...actionStencilBase,
            nextPlayerAction: action.nextPlayerAction,
            nextBallAction: action.nextBallAction,
            motionType: getPossessionMotionType(action),
          };
          return possessionAction;
        }
      }
    }
  );

  /** ======== Marker Stencils ======== */
  const markerStencils: MarkerStencil[] = getMarkerStencils(data).map((marker): MarkerStencil => {
    const { id, color, rotate, label, markerType, pos } = marker;

    const markerStencilBase: MarkerStencilBase = {
      id: id,
      motionType: MotionType.NONE,
      color: color,
      theta: (rotate && convertDegreesToRadians(rotate)) || undefined,
      text: label,
    };

    switch (markerType) {
      case MarkerType.PLAYER:
        return {
          ...markerStencilBase,
          stencilName: "BasketballPlayerStencil",
          playerIdNumber: marker.playerIdNumber,
          playerWithBall: marker.possession,
          point: pos,
          type: marker.fill ? "fill" : "nofill",
          movements: marker.movements,
        };

      case MarkerType.BALL: {
        return {
          ...markerStencilBase,
          motionType: MotionType.BALL,
          stencilName: "PossessionStencil",
          point: marker.pos,
          movements: marker.movements,
        };
      }
      case MarkerType.MISC:
        return { ...markerStencilBase, stencilName: getMiscStencilName(marker), point: pos };
      case MarkerType.TEXT:
        return { ...markerStencilBase, stencilName: "FreeTextStencil", point: pos, width: marker.width };

      case MarkerType.SHADING_RECTANGLE: {
        const { width, height } = marker;
        return {
          ...markerStencilBase,
          stencilName: "RectangleShadingStencil",
          point: [pos, { x: pos.x + width, y: pos.y + height }],
        };
      }
      case MarkerType.SHADING_OVAL: {
        const { rx, ry } = marker;
        return {
          ...markerStencilBase,
          stencilName: "OvalShadingStencil",
          point: [pos, { x: pos.x + rx * 2, y: pos.y + ry * 2 }],
          points: [
            [pos.x - rx, pos.y - ry],
            [pos.x + rx, pos.y - ry],
            [pos.x - rx, pos.y + ry],
            [pos.x + rx, pos.y + ry],
          ],
        };
      }
      case MarkerType.SHADING_TRAPEZOID:
        return { ...markerStencilBase, stencilName: "TrapezoidShadingStencil", point: marker.shadeVertices };
    }
  });

  return JSON.stringify([...actionStencils, ...markerStencils]);
};
